import React, { useState } from 'react';
import { ArrowRight, Lock, Quote } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useReaderAuth } from '../context/ReaderAuthContext';
import { useCourseAccess } from '../hooks/useCourseAccess';
import AuthModal from '../components/AuthModal';
import CourseDashboard from '../components/CourseDashboard';
import { PILLAR_ICON_URLS } from '../components/PillarIcons';

const MODULE_ICONS = [
  { url: PILLAR_ICON_URLS.selfAnchoring,         alt: 'Self-Anchoring icon — anchor symbol representing grounded identity and internal coherence' },
  { url: PILLAR_ICON_URLS.perspectiveShifting,   alt: 'Perspective-Shifting icon — magnifying glass representing cultural insight and perspective-taking' },
  { url: PILLAR_ICON_URLS.adaptiveCommunication, alt: 'Adaptive Communication icon — dialogue symbol representing communication across difference' },
  { url: PILLAR_ICON_URLS.relationalIntegration, alt: 'Relational Integration icon — interlocking puzzle symbol representing shared space and connection' },
];

// Stripe payment links — one per product
const STRIPE_LINKS = {
  'module-2': 'https://buy.stripe.com/cNi7sK71n0eDg3HahJ8so05',
  'module-3': 'https://buy.stripe.com/fZueVcadz1iH7xb9dF8so03',
  'module-4': 'https://buy.stripe.com/dRm8wO5Xje5tdVz0H98so04',
  'bundle':   'https://buy.stripe.com/cNieVc85r7H5dVz3Tl8so07',
  'ebook':    'https://buy.stripe.com/4gM00iadzgdB4kZdtV8so06',
};

const modules = [
  { num: 1, title: 'Self-Anchoring',         desc: 'Establishing your cultural identity as a stable leadership foundation.',                   duration: '45 mins', lessons: 5, free: true  },
  { num: 2, title: 'Perspective-Shifting',   desc: 'Developing the ability to see situations through multiple cultural lenses.',               duration: '60 mins', lessons: 6, free: false, link: STRIPE_LINKS['module-2'] },
  { num: 3, title: 'Adaptive Communication', desc: 'Expressing clearly across cultures without losing your voice.',                            duration: '55 mins', lessons: 5, free: false, link: STRIPE_LINKS['module-3'] },
  { num: 4, title: 'Relational Integration', desc: 'Building trust, influence, and cohesion across cultural boundaries.',                      duration: '50 mins', lessons: 5, free: false, link: STRIPE_LINKS['module-4'] },
];

const INSIGHTS = [
  {
    pillar: 'Self-Anchoring',
    quote: 'You cannot navigate others well if you are not grounded in who you are. Identity is not a barrier to adaptability — it is the foundation of it.',
  },
  {
    pillar: 'Perspective-Shifting',
    quote: 'The ability to see through more than one cultural lens is not relativism. It is disciplined discernment — knowing that meaning is context-dependent.',
  },
  {
    pillar: 'Adaptive Communication',
    quote: 'The goal is not to speak differently. It is to ensure your meaning lands as intended — across the full distance of cultural difference.',
  },
  {
    pillar: 'Relational Integration',
    quote: 'Trust across cultures is not built by good intention alone. It is built when intent lands as intended — consistently, over time.',
  },
  {
    pillar: 'Self-Anchoring',
    quote: 'Third culture individuals often experience identity as tension. Leadership begins by treating that tension as data — not dysfunction. The fluency you built navigating between worlds is your edge.',
  },
  {
    pillar: 'Perspective-Shifting',
    quote: 'When you enter a room carrying a different cultural frame, you are not confused — you are carrying more frames than most. The work is learning when to deploy which one.',
  },
  {
    pillar: 'Adaptive Communication',
    quote: 'High-context cultures communicate around the message. Low-context cultures communicate through it. The fluent leader knows which room they are in — and adjusts without pretending the difference does not exist.',
  },
  {
    pillar: 'Relational Integration',
    quote: 'Cross-cultural teams do not fail from lack of talent. They fail from unexamined assumptions about what professionalism, trust, and initiative look like. Integration begins with making those assumptions visible.',
  },
];

const Courses = () => {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const { user, isAuthenticated, logout, login: contextLogin, register } = useReaderAuth();
  const { isWhitelisted, isLoading: accessLoading } = useCourseAccess();

  const handleLogin  = async (formData) => { const r = await contextLogin(formData.email, formData.password); if (r.success) setIsAuthModalOpen(false); return r; };
  const handleSignup = async (formData) => { const r = await register(formData.email, formData.password, formData.firstName || formData.name?.split(' ')[0] || '', formData.lastName || formData.name?.split(' ').slice(1).join(' ') || ''); if (r.success) setIsAuthModalOpen(false); return r; };
  const openAuth = (mode) => { setAuthMode(mode); setIsAuthModalOpen(true); };

  if (isAuthenticated()) {
    const dashboardUser = { name: user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user?.email?.split('@')[0], email: user?.email };
    return <CourseDashboard user={dashboardUser} onLogout={logout} />;
  }

  return (
    <div className="bg-cream min-h-screen" data-testid="courses-page">

      {/* ── HERO — dark navy for drama ── */}
      <section className="relative pt-32 pb-24 overflow-hidden" style={{ backgroundColor: '#071B3A' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[50%] h-full bg-gradient-to-l from-[#C99316]/[0.02] to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="block text-[11px] tracking-[0.25em] uppercase text-gold font-sans font-semibold mb-6">
            Self-Paced Learning
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-[72px] text-cream font-light tracking-tighter leading-[0.92] mb-6">
            Cultural Fluency<br />
            <span className="text-gold">Mini-Course</span>
          </h1>
          <p className="text-cream/72 text-lg font-light leading-[1.8] tracking-wide max-w-xl mb-10">
            Master cultural intelligence and third culture leadership through practical, self-paced modules.
          </p>
          <div className="flex flex-wrap gap-5">
            <button
              onClick={() => openAuth('signup')}
              data-testid="get-started-btn"
              className="btn-editorial border border-gold text-gold px-8 py-4 text-sm uppercase tracking-[0.12em] font-sans font-medium inline-flex items-center gap-3"
            >
              <span>Get Started Free</span> <ArrowRight size={16} />
            </button>
            <button
              onClick={() => openAuth('login')}
              data-testid="signin-btn"
              className="border-b border-cream/20 pb-1 text-sm uppercase tracking-[0.12em] text-cream/50 font-sans hover:border-gold hover:text-gold transition-all duration-500"
            >
              Sign In
            </button>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/20 to-transparent" />
      </section>

      {/* ── OVERVIEW STATS — ivory ── */}
      <section className="bg-cream py-14">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: '4',           label: 'Modules'         },
              { value: '21',          label: 'Lessons'         },
              { value: '3.5h',        label: 'Total Content'   },
              { value: 'Certificate', label: 'On Completion'   },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-navy/[0.07] p-6 text-center hover:border-gold/20 transition-all duration-500 rounded-xl shadow-sm shadow-navy/[0.03]">
                <p className="font-serif text-3xl text-navy font-light tracking-tight">{s.value}</p>
                <p className="text-navy/45 text-[10px] uppercase tracking-[0.15em] mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CURRICULUM — ivory ── */}
      <section className="bg-[#EDE9E0] py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="block text-[11px] tracking-[0.25em] uppercase text-gold/70 font-sans font-semibold mb-4">Curriculum</span>
          <h2 className="font-serif text-3xl sm:text-4xl text-navy font-light tracking-tighter leading-[1.05] mb-12">
            What You'll Learn
          </h2>

          {/* 2×2 icon grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {modules.map((m) => {
              const ic = MODULE_ICONS[m.num - 1];
              const cardClasses = "bg-white border border-navy/[0.07] rounded-2xl p-8 md:p-10 hover:border-gold/30 hover:shadow-md hover:shadow-navy/[0.06] transition-all duration-500 flex flex-col group shadow-sm shadow-navy/[0.03] cursor-pointer";

              const inner = (
                <>
                  {/* Large pillar icon */}
                  <div className="flex justify-center mb-7">
                    <img
                      src={ic.url}
                      alt={ic.alt}
                      width={120}
                      height={120}
                      style={{ objectFit: 'contain', display: 'block' }}
                      draggable={false}
                    />
                  </div>

                  {/* Number + title */}
                  <span className="block text-[10px] uppercase tracking-[0.22em] text-navy/40 font-sans mb-2">
                    Module 0{m.num}
                  </span>
                  <h3 className="font-serif text-xl text-navy font-light tracking-tight mb-3">{m.title}</h3>
                  <p className="text-navy/65 text-sm font-light leading-[1.75] flex-1">{m.desc}</p>

                  {/* Footer row */}
                  <div className="mt-6 pt-4 border-t border-navy/[0.06] flex items-center justify-between">
                    <div className="flex items-center gap-3 text-navy/42 text-[10px] tracking-wider">
                      <span>{m.duration}</span>
                      <span className="w-1 h-1 rounded-full bg-navy/15" />
                      <span>{m.lessons} lessons</span>
                    </div>
                    {m.free ? (
                      <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-green-600 border border-green-500/25 px-3 py-1.5 rounded group-hover:bg-green-500 group-hover:text-white transition-all">
                        Start Free <ArrowRight size={10} />
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-gold border border-gold/25 px-3 py-1.5 rounded group-hover:bg-gold group-hover:text-navy transition-all">
                        <Lock size={10} /> £10
                      </span>
                    )}
                  </div>
                </>
              );

              return m.free ? (
                <Link
                  key={m.num}
                  to="/module-1"
                  className={cardClasses}
                  style={{ textDecoration: 'none' }}
                  data-testid={`module-${m.num}`}
                >
                  {inner}
                </Link>
              ) : (
                <a
                  key={m.num}
                  href={m.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cardClasses}
                  style={{ textDecoration: 'none' }}
                  data-testid={`module-${m.num}`}
                >
                  {inner}
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── EDUCATIONAL INSIGHTS — teach while they browse ── */}
      <section className="bg-cream py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold/65 font-sans mb-4">
            From the Curriculum
          </p>
          <h2 className="font-serif text-2xl text-navy font-light mb-10">
            A glimpse into each pillar
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="insight-cards">
            {INSIGHTS.map((ins, i) => (
              <div
                key={i}
                className="bg-white border border-navy/[0.07] rounded-xl p-6 hover:border-gold/20 hover:shadow-sm hover:shadow-navy/[0.04] transition-all shadow-sm shadow-navy/[0.03]"
                data-testid={`insight-card-${i}`}
              >
                <p
                  className="text-[10px] uppercase tracking-[0.22em] font-sans font-semibold mb-4"
                  style={{ color: '#C99316' }}
                >
                  {ins.pillar}
                </p>
                <div className="flex gap-3">
                  <Quote size={16} className="text-gold/40 flex-shrink-0 mt-0.5" />
                  <p className="font-serif text-navy/65 text-base leading-[1.7] italic">
                    {ins.quote}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BUNDLE OFFER — ivory ── */}
      <section className="bg-[#EDE9E0] py-12">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="bg-white border border-gold/20 rounded-xl p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 shadow-sm shadow-navy/[0.03]">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-gold/70 font-sans mb-2">Best Value</p>
              <h3 className="font-serif text-xl text-navy font-light">All Three Paid Modules</h3>
              <p className="text-navy/40 text-sm mt-1">Modules 2, 3 &amp; 4 together — save £10 vs buying individually</p>
            </div>
            <div className="flex items-center gap-5 flex-shrink-0">
              <div className="text-right">
                <p className="text-navy/25 text-sm line-through">£30</p>
                <p className="font-serif text-3xl font-light" style={{ color: '#C99316' }}>£20</p>
              </div>
              <a
                href={STRIPE_LINKS['bundle']}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-editorial border border-gold text-gold px-6 py-3 text-[11px] uppercase tracking-[0.12em] font-sans inline-flex items-center gap-2 hover:bg-gold hover:text-navy transition-all"
                data-testid="buy-bundle-btn"
              >
                Get Bundle <ArrowRight size={14} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA — dark navy accent for close ── */}
      <section className="relative py-20 overflow-hidden" style={{ backgroundColor: '#071B3A' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[40%] h-full bg-gradient-to-l from-[#C99316]/[0.02] to-transparent" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl text-cream font-light tracking-tighter leading-[1.05] mb-6">Start Learning Today</h2>
          <p className="text-cream/72 text-base font-light tracking-wide mb-10 max-w-md mx-auto">
            Module 1 is free. Create your account and begin your cultural fluency journey.
          </p>
          <button
            onClick={() => openAuth('signup')}
            className="btn-editorial border border-gold text-gold px-8 py-4 text-sm uppercase tracking-[0.12em] font-sans font-medium inline-flex items-center gap-3"
          >
            <span>Create Free Account</span> <ArrowRight size={16} />
          </button>
          <p className="text-cream/40 text-[11px] mt-6 tracking-wide">No credit card required</p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/15 to-transparent" />
      </section>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onLogin={handleLogin}
        onSignup={handleSignup}
        mode={authMode}
      />
    </div>
  );
};

export default Courses;
